/**
 * What is happening next with a piece of content, and how it got where it is.
 *
 * The timeline's job is to answer three things: when did this start, when
 * should it be finished, and what is it waiting on right now. Lateness is
 * deliberately not emphasised — the activity log already covers that
 * (dokumentasjon/07-utviklingsplan.md 2.1.1).
 */
import { CONTENT_STATUS_LABELS, type ActivityDto, type ContentItemDto } from '@nova/shared';
import { startOfDay } from './timeline.js';

/** The phases a piece of content moves through, in order. */
export const PHASES = ['SUGGESTED', 'APPROVED', 'DRAFT', 'SCHEDULED', 'PUBLISHED'] as const;
export type Phase = (typeof PHASES)[number];

export const PHASE_LABELS: Record<Phase, string> = {
  SUGGESTED: 'Forslag',
  APPROVED: 'Til produksjon',
  DRAFT: 'I arbeid',
  SCHEDULED: 'Planlagt',
  PUBLISHED: 'Publisert',
};

export interface NextActivity {
  /** When the next step is due. Null when nothing is outstanding. */
  at: Date | null;
  /** What the item is waiting for, in Nova's words. */
  label: string;
  /** True once that moment has passed — shown quietly, not as an alarm. */
  passed: boolean;
}

/**
 * What the item is waiting on. This is the column people read first, so it
 * names the *action* someone has to take, not the state it sits in.
 */
export function nextActivity(item: ContentItemDto, now: Date = new Date()): NextActivity {
  const deadline = item.draftDeadline ? new Date(item.draftDeadline) : null;
  const publish = item.publishAt ? new Date(item.publishAt) : null;

  const targets = item.channelTargets ?? [];
  const confirmed = targets.filter((t) => t.status === 'PUBLISHED_CONFIRMED').length;

  switch (item.status) {
    case 'SUGGESTED':
      return due('Venter på vurdering', deadline, now);
    case 'APPROVED':
      return due('Venter på utkast', deadline, now);
    case 'DRAFT':
      return due('Utkast til gjennomgang', deadline, now);
    case 'SCHEDULED': {
      // Partly out, or past its moment: what remains is confirming it.
      if (confirmed > 0 || (publish && publish <= now)) {
        const remaining = targets.length - confirmed;
        return due(
          remaining > 0 && confirmed > 0
            ? `Venter på bekreftelse (${remaining} av ${targets.length} kanaler)`
            : 'Venter på bekreftelse',
          publish,
          now,
        );
      }
      return due('Venter på publisering', publish, now);
    }
    case 'PUBLISHED':
      return { at: publish, label: 'Publisert', passed: false };
    case 'REJECTED':
      return { at: null, label: 'Avvist', passed: false };
    case 'CANCELLED':
      return { at: null, label: 'Kansellert', passed: false };
    default:
      return { at: null, label: CONTENT_STATUS_LABELS[item.status], passed: false };
  }
}

function due(label: string, at: Date | null, now: Date): NextActivity {
  return { at, label, passed: at !== null && at < now };
}

/**
 * Sort key: the soonest outstanding step first, finished work last.
 * Items with nothing scheduled sink below those with a date, rather than
 * jumping to the top as a zero would.
 */
export function nextActivitySortKey(item: ContentItemDto, now: Date = new Date()): number {
  const next = nextActivity(item, now);
  if (['PUBLISHED', 'REJECTED', 'CANCELLED'].includes(item.status)) {
    return Number.MAX_SAFE_INTEGER - (next.at ? -next.at.getTime() : 0);
  }
  return next.at ? next.at.getTime() : Number.MAX_SAFE_INTEGER - 1;
}

export interface PhaseSegment {
  phase: Phase;
  label: string;
  start: Date;
  end: Date;
  /** Drawn from the activity log rather than guessed at. */
  actual: boolean;
}

const ACTION_TO_PHASE: Record<string, Phase> = {
  'content.suggested': 'SUGGESTED',
  'content.approved': 'APPROVED',
  'content.draft': 'DRAFT',
  'content.scheduled': 'SCHEDULED',
  'content.published': 'PUBLISHED',
};

/**
 * Reconstructs the phases an item has been through from the activity log, and
 * estimates the ones still ahead of it.
 *
 * Estimates are anchored on the two dates a human actually set: the draft
 * deadline (when the draft phase must be over) and the planned publication.
 * Everything between them is spread evenly — honest guesswork, labelled as such.
 */
export function buildPhases(
  item: ContentItemDto,
  activity: ActivityDto[],
  now: Date = new Date(),
): PhaseSegment[] {
  const entered = new Map<Phase, Date>();

  // Oldest first, so a re-entered phase keeps its first occurrence.
  const ordered = [...activity].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  for (const event of ordered) {
    const phase = ACTION_TO_PHASE[event.action];
    if (phase && !entered.has(phase)) entered.set(phase, new Date(event.createdAt));
  }
  if (!entered.has('SUGGESTED')) entered.set('SUGGESTED', new Date(item.createdAt));

  const stopped = ['REJECTED', 'CANCELLED'].includes(item.status);
  const statusIndex = PHASES.indexOf(item.status as Phase);
  // A rejected or cancelled item never went past the phase it died in; we only
  // know it got at least as far as having been suggested.
  const currentIndex = statusIndex >= 0 ? statusIndex : lastEnteredIndex(entered);
  const last = stopped ? currentIndex : PHASES.length - 1;

  const deadline = item.draftDeadline ? startOfDay(item.draftDeadline) : null;
  const publish = item.publishAt ? startOfDay(item.publishAt) : null;
  const today = startOfDay(now);

  /*
   * Boundaries, not spans: b[i] is when phase i began, b[last + 1] when the
   * whole thing ends. Some are known from the log, two are anchored on dates a
   * human set (the draft must be done when SCHEDULED starts; PUBLISHED ends at
   * the planned publication). The rest are interpolated between the ones we know,
   * which is what keeps a passed deadline from collapsing every remaining phase
   * onto today.
   */
  const b: (Date | null)[] = Array.from({ length: last + 2 }, () => null);
  for (const [index, phase] of PHASES.entries()) {
    if (index <= last) b[index] = entered.get(phase) ?? null;
  }
  b[0] ??= startOfDay(item.createdAt);

  if (!stopped) {
    // Anchors only bind ahead of where we actually are: a date that has already
    // passed says what should have happened, not what still can.
    const anchor = (index: number, at: Date | null) => {
      if (!at || index <= currentIndex || b[index]) return;
      b[index] = at < today ? today : at;
    };
    anchor(PHASES.indexOf('SCHEDULED'), deadline);
    b[last + 1] = publish ?? deadline ?? new Date(today.getTime() + 7 * 86_400_000);
    if (b[last + 1]! < today) b[last + 1] = today;
  } else {
    b[last + 1] = entered.get(PHASES[last]!) ?? today;
  }

  // The current phase straddles today: everything before it is behind us,
  // everything after still ahead.
  for (let i = 0; i <= last + 1; i += 1) {
    if (!b[i]) continue;
    if (i <= currentIndex && b[i]! > today) b[i] = today;
    if (i > currentIndex && !stopped && b[i]! < today) b[i] = today;
  }

  fillGaps(b);
  for (let i = 1; i <= last + 1; i += 1) {
    if (b[i]! < b[i - 1]!) b[i] = b[i - 1]!;
  }

  return PHASES.slice(0, last + 1).map((phase, index) => ({
    phase,
    label: PHASE_LABELS[phase],
    start: b[index]!,
    end: b[index + 1]!,
    actual: entered.has(phase) && index <= currentIndex,
  }));
}

function lastEnteredIndex(entered: Map<Phase, Date>): number {
  let index = 0;
  for (const [i, phase] of PHASES.entries()) if (entered.has(phase)) index = i;
  return index;
}

/** Spreads unknown boundaries evenly between the two known ones around them. */
function fillGaps(b: (Date | null)[]): void {
  let known = 0;
  for (let i = 1; i < b.length; i += 1) {
    if (!b[i]) continue;
    const gap = i - known;
    if (gap > 1) {
      const from = b[known]!.getTime();
      const step = (b[i]!.getTime() - from) / gap;
      for (let j = known + 1; j < i; j += 1) b[j] = new Date(from + step * (j - known));
    }
    known = i;
  }
  // Nothing known beyond this point — leave the tail sitting on the last one.
  for (let i = known + 1; i < b.length; i += 1) b[i] = b[known]!;
}
